import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SEO from '../components/SEO';
import './FAQPage.css';

const faqs = [
  {
    category: 'Trips & Booking',
    items: [
      {
        question: 'How do I book a diving trip?',
        answer: 'Browse our trips page, find one that suits you, and click "View Details". From there you can submit an application. Once approved by the trip organiser, you\'ll receive a confirmation email with next steps.',
      },
      {
        question: 'Can I cancel or change my booking?',
        answer: 'Yes. Contact us at least 30 days before the trip start date for a full refund. Cancellations within 14–30 days receive a 50% refund. Within 14 days, unfortunately no refund can be issued. You can always transfer your spot to another diver.',
      },
      {
        question: 'How many people are on each trip?',
        answer: 'Group sizes vary by trip and are listed on each trip\'s detail page. We keep groups small — typically 6 to 12 divers — to ensure a quality experience and minimal environmental impact.',
      },
      {
        question: 'What is included in the trip price?',
        answer: 'Each trip listing clearly states what\'s included. Typically this covers guided dives, tanks, and weights. Flights, accommodation, and meals are often separate unless explicitly stated. Always check the trip details before booking.',
      },
    ],
  },
  {
    category: 'Diving Requirements',
    items: [
      {
        question: 'What certification do I need?',
        answer: 'Most trips require at least an Open Water Diver certification (PADI, SSI, NAUI, or equivalent). Some advanced trips require Advanced Open Water or specific specialty certifications. Requirements are listed on each trip\'s page.',
      },
      {
        question: 'I\'m a beginner — are there trips for me?',
        answer: 'Absolutely. We offer trips designed specifically for beginners and newly certified divers, with shallower depths, calmer conditions, and extra guidance from our instructors.',
      },
      {
        question: 'Do I need to bring my own equipment?',
        answer: 'Not necessarily. Most trips offer full equipment rental. If you prefer to dive with your own gear, you\'re welcome to bring it. Check the specific trip details for what\'s available on-site.',
      },
      {
        question: 'Are there age or health requirements?',
        answer: 'Divers must be at least 18 years old (or 12–17 with parental consent for junior programmes). You should be in good general health. Certain medical conditions may require a doctor\'s sign-off — we\'ll send you a health questionnaire after booking.',
      },
    ],
  },
  {
    category: 'Account & Profile',
    items: [
      {
        question: 'How do I create an account?',
        answer: 'Click "Join Now" on the home page or "Register" in the navigation. Fill in your details and verify your email address. It\'s completely free.',
      },
      {
        question: 'How do I update my certification details?',
        answer: 'Go to your Profile page after logging in. You can add your certification level, agency, and diver number there. Keeping this up to date helps trip organisers approve your applications faster.',
      },
      {
        question: 'I forgot my password. What do I do?',
        answer: 'Click "Forgot Password" on the login page and enter your email address. You\'ll receive a reset link within a few minutes. Check your spam folder if it doesn\'t arrive.',
      },
    ],
  },
  {
    category: 'Safety & Insurance',
    items: [
      {
        question: 'Is diving insurance required?',
        answer: 'We strongly recommend having valid dive insurance (such as DAN — Divers Alert Network) before joining any trip. Some trips make it mandatory. Check the individual trip requirements.',
      },
      {
        question: 'What safety measures are in place?',
        answer: 'All trips are led by certified dive professionals. Pre-dive safety briefings, buddy systems, and emergency oxygen are standard on every trip. Our guides are trained in first aid and rescue diving.',
      },
    ],
  },
];

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-question" onClick={() => setOpen(!open)}>
        <span>{question}</span>
        {open ? <FaChevronUp className="faq-icon" /> : <FaChevronDown className="faq-icon" />}
      </button>
      {open && <div className="faq-answer">{answer}</div>}
    </div>
  );
};

const FAQPage = () => {
  return (
    <div className="faq-page">
      <SEO
        title="FAQ - DiveConnect"
        description="Answers to the most common questions about booking diving trips, certification requirements, safety, and your DiveConnect account."
      />

      {/* Hero */}
      <div className="faq-hero">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h1 className="faq-hero-title">Frequently Asked Questions</h1>
              <p className="faq-hero-subtitle">
                Everything you need to know before your next dive adventure.
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* FAQ Content */}
      <section className="faq-content">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              {faqs.map((group) => (
                <div key={group.category} className="faq-group">
                  <h2 className="faq-category">{group.category}</h2>
                  {group.items.map((item) => (
                    <FAQItem key={item.question} question={item.question} answer={item.answer} />
                  ))}
                </div>
              ))}

              <div className="faq-contact-prompt">
                <p>Still have a question?</p>
                <a href="mailto:info@dive-connect.com" className="btn btn-primary">
                  Email Us
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default FAQPage;
